import { Check, XClose } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { type ReactNode, useContext, useId } from "react";

import { CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";

import Button from "../../Button";
import { ComboboxContext, ComboboxSelectedContext } from "./context";

export type ComboboxOptionProps<TType = string> = {
  children: ReactNode;
  selected?: boolean;
  value: TType;
  disabled?: boolean;
};

export default function ComboboxOption<TType = string>({
  children,
  value,
  selected,
  disabled = false,
}: ComboboxOptionProps<TType>) {
  const { multiple, removable, onSelect, onRemove, truncate } =
    useContext(ComboboxContext);
  const selectedOption = useContext(ComboboxSelectedContext);
  const id = useId();

  return selectedOption ? (
    <span
      className={classNames(
        "inline-flex items-center gap-1",
        multiple &&
          "rounded-md border border-gray-300 py-px ps-2.5 pe-2 text-sm",
        truncate && "min-w-0",
      )}
    >
      {truncate ? <span className="truncate">{children}</span> : children}
      {multiple && removable && (
        <Button
          color="link-gray"
          className="ms-auto"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(value);
          }}
        >
          <XClose className="size-3 text-gray-400" />
        </Button>
      )}
    </span>
  ) : (
    <CommandItem
      value={id}
      disabled={disabled}
      onSelect={() => {
        if (disabled) return;
        if (selected) {
          onRemove(value);
        } else {
          onSelect(value);
        }
      }}
      className={cn(
        "flex cursor-pointer items-center p-2.5 ps-2 hover:bg-gray-50 data-focus:bg-gray-50",
        selected && "bg-gray-50",
      )}
    >
      {children}
      {selected && <Check className="text-primary-600 ms-auto size-5" />}
    </CommandItem>
  );
}
